/**
 * Every user-facing string in the admin console.
 *
 * In two languages like everything else, and for a less obvious reason than
 * elsewhere: the people who will eventually run support are not necessarily the
 * people who built it, and a console that is English-only quietly decides who
 * can be hired to operate it.
 */
export const en = {
  "app.name": "Spontar",
  "app.admin": "Admin",

  "nav.dashboard": "Overview",
  "nav.institutions": "Institutions",
  "nav.users": "People",
  "nav.legal": "Legal library",
  "nav.ai": "Assistant cost",
  "nav.retention": "Retention",
  "nav.health": "Health",
  "nav.migrations": "Migrations",
  "nav.support": "Support access",
  "nav.enquiries": "Enquiries",
  "nav.enquiries.unanswered": "{count} unanswered",
  "nav.signOut": "Sign out",
  "nav.menu": "Menu",
  "nav.skipToContent": "Skip to content",

  "zone.label": "Zone",
  "zone.eu": "EU",
  "zone.us": "US",
  "zone.explain":
    "Which deployment this console is acting on. Every institution lives in exactly one, and moving between them is a migration rather than a setting.",

  "auth.signIn": "Sign in",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signingIn": "Signing in…",
  "auth.staffOnly": "This console is for Spontar staff.",

  "mfa.title": "Set up a second factor",
  "mfa.body":
    "This console can read every institution's data and change what any of them is entitled to. A password on its own is not enough to hold that, so a second factor is required before anything else can be done.",
  "mfa.phone": "Phone number",
  "mfa.phoneHint": "A code is sent by SMS each time you sign in.",
  "mfa.send": "Send a code",
  "mfa.code": "The code",
  "mfa.verify": "Finish enrolment",
  "mfa.enrolled": "Enrolled. Sign in again — you will be asked for a code from now on.",
  "mfa.required": "Enrol a second factor before continuing.",
  "mfa.signOutInstead": "Sign out instead",
  "mfa.reauth.title": "Please confirm it’s you",
  "mfa.reauth.body":
    "You have been signed in for a while. Setting up a second factor is sensitive enough that we need your password once more before it can be added.",
  "mfa.reauth.confirm": "Confirm and continue",
  "mfa.reauth.wrong": "That password is not right.",

  "error.notAdmin.title": "Not for you",
  "error.notAdmin.body":
    "This console is for Spontar staff. If you administer an institution, your own back office is where you work.",
  "error.notAdmin.toBackOffice": "Go to the back office",

  "mfa.unverified.title": "Your address has not been confirmed",
  "mfa.unverified.body":
    "A second factor cannot be added to an unconfirmed address. That is Firebase's rule, not ours, and it is a reasonable one: without it, anybody who took the account could lock its owner out of it.",
  "mfa.unverified.send": "Send me the link",
  "mfa.unverified.sent": "Sent",
  "mfa.unverified.then": "Open it, then come back here and start again.",
  "mfa.verificationSent": "Check your email.",
  "auth.mfa.title": "One more step",
  "auth.mfa.body": "A code has to go to your phone ending {hint} before you can come in.",
  "auth.mfa.send": "Send the code",
  "auth.mfa.code": "Code",
  "auth.mfa.verify": "Sign in",
  "auth.mfa.startOver": "Start again",
  "error.auth.invalidCode": "That code is not right. Ask for another if it has expired.",

  "common.save": "Save",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.loading": "Loading…",
  "common.retry": "Try again",
  "common.language": "Language",
  "common.locale.de": "Deutsch",
  "common.locale.en": "English",
  "billing.plan.title": "Plan",
  "tier.solo": "Solo — 1 teacher",
  "tier.small": "Small — up to 3 teachers",
  "tier.medium": "Medium — up to 10 teachers",
  "tier.large": "Large — up to 40 teachers",
  "common.search": "Search",
  "common.none": "None",
  "common.back": "Back",
  "common.required": "Required",
  "common.optional": "Optional",
  "common.saved": "Saved",
  "common.add": "Add",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.yes": "Yes",
  "common.no": "No",

  "dashboard.title": "Overview",
  "dashboard.welcome": "Hello, {name}.",

  "audit.reason": "Why",
  "audit.reasonHint":
    "Recorded against your name in the audit log, permanently. A sentence a colleague could read in a year and understand.",
  "audit.reasonRequired": "A reason is required for anything destructive.",

  "error.unexpected": "Something went wrong. It has been logged.",
  "error.network.unavailable": "We could not reach the server. Try again.",
  "error.auth.required": "Please sign in.",
  "error.auth.invalidCredentials": "That email and password do not match.",
  "error.auth.staleLogin": "For security, please enter your password again before setting up a second factor.",
  "error.permission.denied": "You do not have access to this.",
  "error.permission.disabled": "This account has been disabled.",
  "error.permission.noInstitution": "This account is not attached to an institution.",
  "error.validation.invalidInput": "Something in this form is not valid.",
  "error.validation.studentSeatsTooFew": "At least one student place.",
  "error.validation.studentSeatsTooMany": "The most a licence can allow is 10,000 students.",
  "error.user.notStaff": "Only a teacher or an owner can be sent a sign-in link.",
  "error.user.noEmail": "This person has no email address to send a link to.",
  "error.user.noAuthAccount":
    "There is no sign-in account behind this person. Issue a new password instead.",
  "error.notFound.title": "There is nothing here",
  "error.notFound.body": "The link may be old, or the page may have moved.",

  "metrics.institutions": "Institutions",
  "metrics.institutions.detail": "Active, suspended, lapsed, and where they are",
  "metrics.institutions.active": "Active",
  "metrics.institutions.suspended": "Suspended",
  "metrics.institutions.lapsed": "Licence run out",
  "metrics.institutions.byCountry": "By country",

  "metrics.seats": "Seats sold",
  "metrics.seats.detail": "What has been sold, by tier",
  "metrics.seats.tier": "Tier",
  "metrics.seats.sold": "Seats",
  "metrics.seats.institutions": "Institutions",

  "metrics.sessions": "Lessons this month",
  "metrics.sessions.detail": "This week, this month, and how they connected",
  "metrics.sessions.week": "This week",
  "metrics.sessions.month": "This month",
  "metrics.sessions.online": "Online",
  "metrics.sessions.offline": "Offline",

  "metrics.runsPerSession": "Scenes per lesson",
  "metrics.runsPerSession.detail": "The distribution, which is the part that matters",
  "metrics.runsPerSession.why":
    "A lesson with one scene in it is somebody trying the product. Four is somebody teaching with it. A rising lesson count can hide a falling one of these, which is why this is the number to watch.",
  "metrics.runsPerSession.runs": "Scenes",
  "metrics.runsPerSession.sessions": "Lessons",

  "metrics.ai": "Assistant cost this month",
  "metrics.ai.detail": "By feature, against last month",
  "metrics.ai.previous": "Last month",
  "metrics.ai.feature": "Feature",
  "metrics.ai.cost": "Cost",
  "metrics.ai.refused": "Refused by the boundary",

  "metrics.empty": "Nothing to show yet",
  "metrics.openDetail": "Show the breakdown",

  "alerts.retentionOverdue":
    "{count} sessions are past their retention window and were not deleted.",
  "alerts.retentionMissed": "The deletion job has not finished a run in the last day.",
  "alerts.aiBoundaryViolation":
    "{count} requests to the assistant were refused because they carried student data. This should be impossible; treat it as a P1.",
  "alerts.aiBudgetExhausted": "{count} institutions have used up this month's assistant budget.",
  "alerts.legalGap":
    "{count} required documents apply to a customer and have not been published. That blocks a sale, not a lesson.",

  "institutions.title": "Institutions",
  "institutions.new": "Add an institution",
  "institutions.empty": "No institution yet",
  "institutions.column.name": "Name",
  "institutions.column.country": "Country",
  "institutions.column.zone": "Zone",
  "institutions.column.segment": "Kind",
  "institutions.column.tier": "Tier",
  "institutions.column.seats": "Seats",
  "institutions.column.validUntil": "Runs until",
  "institutions.column.status": "Status",
  "institutions.filter.status": "Status",
  "institutions.filter.country": "Country",
  "institutions.filter.tier": "Tier",
  "institutions.filter.all": "All",
  "institutions.state.active": "Active",
  "institutions.state.expiring": "Expiring",
  "institutions.state.lapsed": "Run out",
  "institutions.state.suspended": "Suspended",
  "institutions.segment.studio": "Studio",
  "institutions.segment.school": "School",

  "institution.new.title": "Add an institution",
  "institution.new.name": "Name",
  "institution.new.country": "Country",
  "institution.new.zone": "Zone",
  "institution.new.zoneDerived":
    "Derived from the country, never chosen. Zone decides where the data sits and which law reaches it.",
  "institution.new.zoneUsUnavailable":
    "The US deployment does not exist yet. American and Canadian institutions currently live in the EU one, which is a decision worth telling them about rather than letting them discover.",
  "institution.new.segment": "Kind of institution",
  "institution.new.locale": "Language",
  "institution.new.tier": "Tier",
  "institution.new.seats": "Teacher seats",
  "institution.new.students": "Student places",
  "institution.new.studentsHint":
    "How many students the licence allows. Teachers and the owner are not counted.",
  "institution.new.validUntil": "Runs until",
  "institution.new.ownerName": "The owner's name",
  "institution.new.ownerEmail": "The owner's email",
  "institution.new.billingEmail": "Where invoices go",
  "institution.new.create": "Create the institution",
  "institution.new.created": "{name} created.",
  "institution.new.linkSent": "A sign-in link is on its way to {email}.",
  "institution.new.linkNotSent":
    "{name} was created, but the sign-in link could not be sent to {email}. Open the institution and press “Send sign-in link”.",

  "institution.tab.overview": "Overview",
  "institution.tab.licence": "Licence",
  "institution.tab.usage": "Usage",
  "institution.tab.users": "People",
  "institution.tab.legal": "Legal",
  "institution.tab.danger": "Danger zone",

  "institution.overview.created": "Created",
  "institution.overview.billingEmail": "Invoices go to",
  "institution.overview.vat": "VAT identification number",
  "institution.overview.vatValidated": "Checked on {date}",
  "institution.overview.vatNone": "None on file",
  "institution.overview.manualBilling": "Billed by invoice",
  "institution.overview.stripe": "Billed by card",

  "institution.licence.title": "Set the licence by hand",
  "institution.licence.body":
    "This is the whole billing path for a school paying by bank transfer. The money arrives, and somebody sets the date here. There is no separate invoicing system.",
  "institution.licence.tier": "Tier",
  "institution.licence.seats": "Teacher seats",
  "institution.licence.students": "Student places",
  "institution.licence.studentsUnset":
    "Not set yet. Until it is, this licence allows any number of students — set one now.",
  "institution.licence.validUntil": "Runs until",
  "institution.licence.apply": "Set it",
  "institution.licence.applied": "Licence set.",
  "institution.licence.seatsExceedTier": "That is more seats than the {tier} tier allows.",
  "institution.licence.seatsTooFew": "At least one seat — the owner holds it.",
  "institution.licence.studentsRequired": "How many students may this licence hold? Required.",
  "institution.licence.studentsTooFew": "At least one.",
  "institution.licence.studentsTooMany": "The most a licence can allow is {max} students.",

  "institution.usage.teacherSeats": "Teacher seats",
  "institution.usage.teachersUsed": "Teachers, including the owner",
  "institution.usage.studentSeats": "Student places",
  "institution.usage.studentsUsed": "Students on the register",
  "institution.usage.unlimited": "No limit set",

  "institution.access.title": "Owner sign-in",
  "institution.access.body":
    "The owner chooses a password through a link sent to their address; nothing else opens the account. Send it again if the mail did not arrive or the link has run out — it works for one hour.",
  "institution.access.send": "Send sign-in link",
  "institution.access.sent": "A sign-in link is on its way to {email}.",
  "institution.access.notSent":
    "The sign-in link could not be sent to {email}. The link was made and the mail did not go — check the mail configuration and try again.",
  "institution.access.noOwner": "No owner with an email address.",

  "institution.users.column.name": "Name",
  "institution.users.column.role": "Role",
  "institution.users.column.username": "Username",
  "institution.users.column.status": "Status",
  "institution.users.noContent":
    "People and their access. Nothing they wrote — a scene, a note, a score — is reachable from here.",

  "institution.legal.applies": "Applies",
  "institution.legal.accepted": "Accepted",
  "institution.legal.outstanding": "Outstanding",
  "institution.legal.blocking": "{count} outstanding, which blocks lessons",
  "institution.legal.ours":
    "{count} documents that apply here have never been published by Spontar. This institution cannot start a lesson, and nobody there can do anything about it.",
  "institution.legal.theirs":
    "{count} documents are published and waiting for this institution to accept them.",
  "institution.legal.unknown":
    "This institution is not in the coverage report. That should not happen — check the Legal library.",
  "institution.legal.version": "version {version}",
  "institution.legal.blockedGroups":
    "{count} active groups cannot start a lesson until this is resolved.",
  "institution.legal.toCoverage": "Open the coverage report",
  "institution.legal.clear": "Everything that applies has been accepted",

  "institution.danger.suspend": "Suspend",
  "institution.danger.reactivate": "Reactivate",
  "institution.danger.suspendTitle": "Suspend {name}",
  "institution.danger.suspendBody":
    "New lessons cannot be started. A lesson already running is unaffected, and nothing is deleted — every scene, group and statistic stays exactly as it is, and reactivating restores everything at once.",
  "institution.danger.suspendConfirm": "Type {name} to confirm",
  "institution.danger.suspended": "Suspended.",
  "institution.danger.reactivated": "Reactivated.",

  "institution.danger.exportFirst": "Download everything first",
  "institution.danger.exportBody":
    "An institution that leaves is entitled to what it made. Take the export before you delete, because afterwards there is nothing to take.",
  "institution.danger.exported": "Downloaded.",

  "institution.danger.delete": "Delete this institution",
  "institution.danger.deleteTitle": "Delete {name}",
  "institution.danger.deleteBody":
    "Every account, group, scene, lesson and statistic belonging to this institution is removed, and every person in it loses the ability to sign in. Nobody can undo this, including us. The audit entry and the reason you type are the only things that will remain.",
  "institution.danger.deleteConfirm": "Type {name} to confirm",
  "institution.danger.deleted": "{name} was deleted.",
  "institution.danger.mismatch": "That is not the name.",

  "enquiries.title": "Enquiries",
  "enquiries.intro":
    "What arrives through the website's contact form. The page promises that a person answers within a working day; mark an enquiry answered once you have, so nobody answers it twice and nothing waits unnoticed.",
  "enquiries.unanswered": "{count} unanswered",
  "enquiries.showClosed": "Show closed",
  "enquiries.hideClosed": "Hide closed",
  "enquiries.empty": "Nothing has arrived yet",
  "enquiries.failed": "The enquiries could not be loaded.",
  "enquiries.column.received": "Received",
  "enquiries.column.from": "From",
  "enquiries.column.about": "About",
  "enquiries.column.message": "Message",
  "enquiries.column.status": "State",
  "enquiries.column.actions": "Actions",
  "enquiries.status.new": "Unanswered",
  "enquiries.status.answered": "Answered",
  "enquiries.status.closed": "Closed",
  "enquiries.action.answered": "Mark answered",
  "enquiries.action.close": "Close",
  "enquiries.action.reopen": "Reopen",
  "enquiries.reply": "Reply by email",
  "enquiries.urgent": "Data protection — answer first",
  "enquiries.subject.general": "General",
  "enquiries.subject.studios": "Using it in a studio",
  "enquiries.subject.schools": "Using it in a school",
  "enquiries.subject.dataProtection": "Data protection",
  "enquiries.subject.press": "Press",
  "enquiries.segment.studio": "Studio",
  "enquiries.segment.school": "School",
  "enquiries.segment.other": "Other",
  "enquiries.updated": "Marked {status}.",
  "enquiries.error.invalid": "That change is not one this page can make.",
  "enquiries.error.notFound": "That enquiry could not be found.",

  "support.title": "Support access",
  "support.intro":
    "Spontar cannot enter an institution on its own authority. You ask; an owner says yes or no; the access is read-only and ends by itself. The security rules are what enforce that, not this page.",
  "support.request": "Ask for access",
  "support.institution": "Institution",
  "support.reason": "Why you need it",
  "support.reasonHint":
    "The owner reads this and decides. Say what you are trying to fix and what you expect to look at — at least a sentence.",
  "support.duration": "For how long",
  "support.duration.1": "One hour",
  "support.duration.8": "A working day",
  "support.duration.24": "A day",
  "support.send": "Send the request",
  "support.sent": "Asked. {count} owners were emailed.",
  "support.readOnly":
    "Read-only, always. There is no write path at all — not a hidden button, not a disabled one.",
  "support.noOperational":
    "A teacher's own record of a lesson — their notes, their scores — stays with them. Access reaches an institution's configuration and its content, and stops there.",

  "support.state.pending": "Waiting for the owner",
  "support.state.approved": "Open",
  "support.state.denied": "Refused",
  "support.state.revoked": "Withdrawn",
  "support.state.expired": "Closed",

  "support.column.institution": "Institution",
  "support.column.reason": "Reason",
  "support.column.asked": "Asked",
  "support.column.state": "State",
  "support.column.remaining": "Left",
  "support.empty": "Nobody has asked for access",
  "support.remaining": "{count} minutes left",

  "support.active.banner":
    "You are inside {institution} until it closes, {minutes} minutes from now. Reason given: {reason}",
  "support.active.readOnly": "Read-only.",

  "support.error.requestNotFound": "That request could not be found.",
  "support.error.alreadyDecided": "That request has already been answered.",
  "support.error.notApproved": "That access is not open.",
  "support.error.notActive": "That access has closed.",

  "users.title": "People",
  "users.search": "Search by name, username, email or id",
  "users.searchHint":
    "Across every institution. This is the one place in the console that spans tenants, because a support question usually arrives as a name and nothing else.",
  "users.column.name": "Name",
  "users.column.role": "Role",
  "users.column.institution": "Institution",
  "users.column.username": "Username",
  "users.column.status": "Status",
  "users.empty": "Nobody matches",
  "users.typeToSearch": "Type at least two characters",
  "users.noContent":
    "Accounts and access. Nothing anybody wrote is reachable from here, with or without support access.",
  "users.resetPassword": "Issue a new password",
  "users.disable": "Disable",
  "users.enable": "Enable",
  "users.disableTitle": "Disable {name}",
  "users.disableBody":
    "They cannot sign in. Nothing is deleted, and enabling them again restores everything at once.",
  "users.confirm": "Type {name} to confirm",
  "users.mismatch": "That is not the name.",
  "users.passwordIssued": "New password for {name}: {password}",
  "users.passwordIssuedWarning": "Shown once. Write it down now.",

  "retention.title": "Retention",
  "retention.breach": "Data we said we would have deleted is still here",
  "retention.alarm.overdue":
    "{count} sessions are past their institution's window and have not been removed.",
  "retention.alarm.missed":
    "The nightly run has not reported for {hours} hours. A job that did not run looks exactly like a job that had nothing to do.",
  "retention.alarm.failed": "The last run reported {failures} failures it could not get past.",
  "retention.overdueLine": "{count} sessions past a {days}-day window",
  "retention.clear": "Nothing is past its window.",
  "retention.clearExplain":
    "Checked against the database rather than taken from the last run's own report, which cannot prove its own completeness.",
  "retention.lastRun": "The last run",
  "retention.ranAt": "Started",
  "retention.duration": "Took",
  "retention.seconds": "{seconds} s",
  "retention.neverFinished": "Never finished",
  "retention.neverRun": "Nothing has ever run. That is the most serious state on this page.",
  "retention.sessionsDeleted": "Sessions removed",
  "retention.eventsDeleted": "Records removed",
  "retention.manual": "Run it now, for one institution",
  "retention.manualExplain":
    "For answering a subject request: the honest answer is easier to give after the window has been applied than a few hours before the nightly run would have applied it. Recorded against your name.",
  "retention.institution": "Institution",
  "retention.run": "Apply the window now",
  "retention.ran": "Done. {count} sessions removed.",

  "health.title": "Health",
  "health.jobsHealthy": "All {count} scheduled jobs are reporting.",
  "health.jobsUnhealthy": "{count} scheduled jobs need attention.",
  "health.jobsExplain":
    "Absence is monitored, not only failure: a job that did not run is indistinguishable from a job that had nothing to do, so this list is driven by the jobs that should exist rather than by the records that do.",
  "health.jobs": "Scheduled jobs",
  "health.job.ok": "Reporting",
  "health.job.failing": "Failing ({count} in a row)",
  "health.job.missed": "Has not reported",
  "health.job.never": "Has never run",
  "health.column.job": "Job",
  "health.column.state": "State",
  "health.column.lastRun": "Last finished",
  "health.column.duration": "Took",
  "health.column.expected": "Expected",
  "health.everyHours": "every {hours} h",
  "health.sessions": "Lessons in progress",
  "health.sessionsNow": "{active} running now — {online} online, {offline} offline.",
  "health.sessionsPrivacy":
    "A count, never a list. Which classes are mid-lesson is the institution's business, and reading that takes a grant its owner gave.",
  "health.counts": "Today",
  "health.countsExplain":
    "Aggregate numbers only — never who, never what. Firestore rule denials are not here: they are a Cloud Monitoring metric and no API hands them to a function. Refused calls measure a related thing and are labelled as themselves.",
  "health.metric.callRefused": "Calls refused",
  "health.metric.emailSent": "Emails sent",
  "health.metric.emailFailed": "Emails failed",
  "health.metric.webhookFailed": "Webhooks failed",
  "health.metric.webhookHandled": "Webhooks handled",
  "health.spike": "Far above the last fortnight. Worth a look.",
  "health.fortnight": "{total} in the last fortnight",

  "migration.title": "Migrations",
  "migration.intro":
    "A dry run is required before a real one, and the server is what refuses — not this page. The dry run has to be the most recent thing that happened to a migration, because one from before the last live run describes a database that no longer exists.",
  "migration.idempotent":
    "Every migration is written to be idempotent and is run twice in the test suite to prove it: a second zone will eventually mean running all of them again from the beginning.",
  "migration.empty": "Nothing to migrate",
  "migration.emptyBody": "No migration has been written yet.",
  "migration.dryRun": "Dry run",
  "migration.apply": "Apply",
  "migration.wouldChange": "The dry run found {count} documents to change.",
  "migration.dryRunDone": "Dry run finished. {count} documents would change.",
  "migration.applied": "Applied. {count} documents changed.",
  "migration.blocked.noDryRun": "Dry-run it first.",
  "migration.blocked.dryRunStale": "Dry-run it again — the last thing that happened was not a dry run.",
  "migration.state.never": "Never run",
  "migration.state.dryRunOnly": "Dry-run only",
  "migration.state.applied": "Applied",
  "migration.state.failed": "Last run failed",
  "migration.mode.dry": "Dry run",
  "migration.mode.live": "Applied",
  "migration.ok": "Fine",
  "migration.failed": "Failed",
  "migration.history": "Runs of {id}",
  "migration.column.when": "When",
  "migration.column.mode": "Mode",
  "migration.column.affected": "Documents",
  "migration.column.outcome": "Outcome",
  "migration.confirmTitle": "Apply: {description}",
  "migration.confirmBody":
    "This writes to every document the dry run counted. It is written to be safe to run twice, and it is still a change to many documents at once with nobody watching each.",
  "migration.confirmType": "Type {name} — the number the dry run reported",
  "migration.mismatch": "That is not the number the dry run reported.",
  "migration.error.unknown": "There is no migration by that name.",

  "ai.title": "Assistant cost",
  "ai.boundary.title": "Student data and the model",
  "ai.boundary.clean": "0 boundary violations, all time.",
  "ai.boundary.violated":
    "{count} boundary violations on record. Every one is a defect in our code, not a false positive.",
  "ai.boundary.unaudited": "Nobody has checked yet.",
  "ai.boundary.explain":
    "No model call may carry anything that identifies a student. The type union makes it inexpressible, a runtime guard walks every payload, and a daily audit re-reads the whole usage log and asks again.",
  "ai.boundary.lastAudit": "Last checked {when}: {checked} calls read, none carried student data.",
  "ai.boundary.lastAuditEmpty":
    "Last run {when}, with no calls in the log to read. Anything since has not been checked.",
  "ai.boundary.stale":
    "The daily check last ran {days} days ago. That is a different problem from a violation, and it means this number is out of date.",
  "ai.boundary.at": "{institution} · {feature} · {when}",
  "ai.boundary.reason.guardFired":
    "A call was refused because its payload looked like it identified somebody.",
  "ai.boundary.reason.unknownKind":
    "A call recorded a shape that is not one of the three permitted ones.",
  "ai.boundary.reason.oversizedField":
    "A usage record carried a field long enough to be hiding a payload.",

  "ai.month": "Month",
  "ai.nothing": "Nothing this month",
  "ai.ms": "{ms} ms",
  "ai.callsWithFailures": "{calls} ({failures} failed)",
  "ai.spend.title": "Spend",
  "ai.spend.total": "This month",
  "ai.spend.calls": "Calls",
  "ai.spend.failureRate": "Failed",
  "ai.spend.p90": "Slowest tenth",
  "ai.feature.authoring": "Writing a scene",
  "ai.feature.importMap": "Reading a class list",
  "ai.feature.statsSummary": "Summarising statistics",
  "ai.byFeature": "By what it was for",
  "ai.byModel": "By model",
  "ai.byInstitution": "By institution",
  "ai.column.name": "Name",
  "ai.column.calls": "Calls",
  "ai.column.cost": "Cost",
  "ai.column.share": "Share",
  "ai.column.model": "Model",
  "ai.column.errorRate": "Errors",
  "ai.column.p50": "Median",
  "ai.column.p90": "Slowest tenth",
  "ai.alert.title": "Close to the cap",
  "ai.alert.approaching": "{institution} has spent {spent} of {budget}.",
  "ai.alert.exhausted":
    "{institution} has spent {spent} of {budget}. Their assistant has stopped answering.",
  "ai.outlier.title": "Far above the others",
  "ai.outlier.explain":
    "Usually a stuck retry loop rather than anything deliberate, and worth a look either way.",
  "ai.outlier.line": "{factor}× the median — {spent} against {median}.",
  "ai.health.title": "The provider",
  "ai.health.fallbackNormal":
    "{rate} of class-list imports fell back to the built-in reader. That is the fallback doing its job.",
  "ai.health.fallbackHigh":
    "{rate} of {calls} class-list imports fell back to the built-in reader. Imports are still working — the model is not.",

  "legal.title": "Legal library",
  "legal.intro":
    "Every document the registry describes, whether or not it has been published. A document that exists only in the repository still applies to somebody — and they will be the ones to find out, at the moment they try to start a lesson.",
  "legal.tabs": "Legal sections",
  "legal.tab.documents": "Documents",
  "legal.tab.matrix": "Who gets what",
  "legal.tab.acceptances": "Acceptances",
  "legal.tab.coverage": "Blocked",
  "legal.empty": "Nothing matches those filters",
  "legal.emptyBody": "Widen the filters, or check the registry in the repository.",
  "legal.filter.type": "Type",
  "legal.filter.jurisdiction": "Country",
  "legal.filter.language": "Language",
  "legal.filter.all": "All",
  "legal.column.document": "Document",
  "legal.column.type": "Type",
  "legal.column.jurisdictions": "Countries",
  "legal.column.segments": "Applies to",
  "legal.column.version": "Version",
  "legal.column.state": "State",
  "legal.column.published": "Published",
  "legal.column.action": "Action",
  "legal.column.required": "Blocks lessons",
  "legal.versionOf": "{published} published, {registry} in the registry",
  "legal.versionNone": "{registry} in the registry",
  "legal.publishAction": "Publish",
  "legal.published": "Published. {affected} institutions affected, {notified} owners emailed.",

  "legal.type.tos": "Terms of service",
  "legal.type.dpa": "Data processing agreement",
  "legal.type.avv": "Auftragsverarbeitungsvertrag",
  "legal.type.ferpa_addendum": "FERPA addendum",
  "legal.type.state_addendum": "State addendum",
  "legal.type.privacy": "Privacy notice",
  "legal.type.privacy_child": "Privacy notice for young people",
  "legal.type.impressum": "Imprint",
  "legal.type.parental_consent_template": "Parental consent template",
  "legal.type.law25_addendum": "Law 25 addendum",

  "legal.state.inReview": "With the lawyers",
  "legal.state.unpublished": "Never published",
  "legal.state.current": "Published and current",
  "legal.state.behind": "A version behind the registry",
  "legal.state.diverged": "Ahead of the registry",

  "legal.blocked.inReview":
    "Not approved yet. Publishing it would put text nobody has signed off behind an \u201cI accept\u201d.",
  "legal.blocked.versionMismatch":
    "Publishing would produce version {version}, and the website prints {registry}. Change legal/documents.json in the repository and deploy the site first \u2014 an institution shown one version and recorded as accepting another is the defect this check exists for.",
  "legal.blocked.noDate": "The registry has no effective date for this document.",

  "legal.publish.title": "Publish {title}",
  "legal.publish.irreversible":
    "A published version cannot be edited or withdrawn. Correcting a mistake means publishing another version.",
  "legal.publish.consequence":
    "Every institution this applies to that accepted an earlier version has to read and accept it again before they can start another lesson. Their owners are emailed.",
  "legal.publish.fromRegistry":
    "These values come from legal/documents.json. Nothing here is typed by hand, because a mistyped country list would publish.",
  "legal.publish.preview": "What they will read",
  "legal.publish.previewUnavailable":
    "The page could not be fetched. Publishing is still possible; nobody would recommend it.",
  "legal.publish.typeVersion": "Type {version} to publish",
  "legal.publish.mismatch": "That is not the version number.",
  "legal.publish.confirm": "Publish version {version}",

  "legal.matrix.title": "Who gets what",
  "legal.matrix.intro":
    "Which documents apply to an institution follows from its country and what it is \u2014 nobody chooses. This is the single highest-risk configuration in the product: a wrong cell means an institution operating without a data-processing agreement.",
  "legal.matrix.readOnly":
    "Read-only, and resolved by the backend rather than by this page. The table lives in reviewed code with a test over every row; a console that could edit it would be a second authority nobody diffs.",
  "legal.matrix.preview": "Show me one",
  "legal.matrix.previewIntro":
    "Pick a profile you know the answer for and read what comes back. This is how the table gets verified.",
  "legal.matrix.country": "Country",
  "legal.matrix.segment": "Kind",
  "legal.matrix.resolvedFor": "A {country} {segment} resolves to {count} documents.",
  "legal.matrix.required": "Blocks lessons",
  "legal.matrix.reference": "For reference",
  "legal.matrix.none": "Does not apply",
  "legal.matrix.publishedAs": "{docId}, version {version}",
  "legal.matrix.notPublished": "Nothing published",
  "legal.matrix.unexpected":
    "Published for this profile but not required by it: {documents}. Somebody is being asked to sign something that does not apply to them.",
  "legal.matrix.sound": "Everything this profile needs is published.",
  "legal.matrix.unsound":
    "{count} required documents have nothing published. Institutions on this profile cannot be onboarded.",
  "legal.matrix.wholeTable": "Every country",
  "legal.matrix.legend": "{required} blocks lessons · {reference} for reference · {none} does not apply",

  "legal.acceptances.title": "Acceptances",
  "legal.acceptances.intro":
    "Who agreed to what, when, and from which address. This is the answer to a data-protection officer asking what their institution signed.",
  "legal.acceptances.immutable":
    "Read-only, and not because the buttons were left out: nothing may create, change or delete one of these records. Evidence anybody can edit is evidence of nothing.",
  "legal.acceptances.search": "Search",
  "legal.acceptances.searchHint": "Institution, document, or the identifier of whoever accepted.",
  "legal.acceptances.export": "Export {count} rows",
  "legal.acceptances.empty": "Nothing matches",
  "legal.acceptances.emptyBody": "Nobody has accepted anything matching that.",
  "legal.acceptances.column.institution": "Institution",
  "legal.acceptances.column.institutionId": "Institution ID",
  "legal.acceptances.column.uid": "Accepted by",
  "legal.acceptances.column.docId": "Document",
  "legal.acceptances.column.version": "Version",
  "legal.acceptances.column.acceptedAt": "When",
  "legal.acceptances.column.ip": "From",

  "legal.coverage.title": "Blocked from teaching",
  "legal.coverage.intro":
    "Institutions that cannot start a lesson because of an outstanding document. Somebody there has a class in front of them, so this is a queue rather than a report.",
  "legal.coverage.institutions": "Institutions",
  "legal.coverage.blockedInstitutions": "Blocked",
  "legal.coverage.blockedGroups": "Groups that cannot start",
  "legal.coverage.oursCount": "Waiting on us",
  "legal.coverage.gapsTitle": "Documents nobody has published",
  "legal.coverage.gapsBody":
    "These are ours, not theirs. The institutions waiting for them cannot do anything about it, and no email will help.",
  "legal.coverage.search": "Search",
  "legal.coverage.empty": "Nobody is blocked",
  "legal.coverage.emptyBody": "Every institution has accepted everything that applies to it.",
  "legal.coverage.column.institution": "Institution",
  "legal.coverage.column.profile": "Profile",
  "legal.coverage.column.fault": "Waiting on",
  "legal.coverage.column.outstanding": "What is missing",
  "legal.coverage.column.blocked": "Groups blocked",
  "legal.coverage.profile": "{country} {segment}",
  "legal.coverage.ours": "Us",
  "legal.coverage.theirs": "Them",
  "legal.coverage.clear": "Nothing",
  "legal.coverage.blockedOf": "{blocked} of {active}",

  "common.language.en": "English",
  "common.language.de": "German",
} as const;

export type MessageKey = keyof typeof en;
